import { useObservableCallback } from 'hooks/rx-hooks'
import * as React from 'react'
import cx from 'classnames'
import Icon from 'components/Icon'
import { animationFrameScheduler, Observable } from 'rxjs'
import { observeOn } from 'rxjs/operators'
import { mergeRefs } from 'utils/react-util'
import styles from './Section.sass'
import Tooltip from 'components/Tooltip'

interface SectionProps {
  children?: React.ReactNode
  collapsible?: boolean
  collapsed?: boolean
  onToggleCollapse?: () => unknown
  scrollMargin?: number
  innerRef?: React.MutableRefObject<HTMLDivElement | null>
  scrollIntoView$?: Observable<unknown>
  dataTest?: string
}

export function Section({
  collapsible,
  collapsed,
  children,
  onToggleCollapse,
  innerRef,
  scrollMargin,
  scrollIntoView$,
  dataTest,
}: SectionProps) {
  const isCollapsed = collapsible && collapsed
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const ref = mergeRefs([innerRef, rootRef])
  useObservableCallback(
    scrollIntoView$?.pipe(observeOn(animationFrameScheduler)),
    () =>
      rootRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      }),
    [scrollIntoView$],
  )
  return (
    <div
      className={cx(styles.root, isCollapsed && styles.collapsed)}
      ref={ref}
      style={{ scrollMarginTop: scrollMargin }}
      data-test={dataTest}
    >
      {React.Children.map(children, (c) =>
        React.cloneElement(c as any, {
          collapsible,
          collapsed,
          onToggleCollapse,
        }),
      )}
    </div>
  )
}

interface SectionHeaderProps
  extends Pick<SectionProps, 'collapsed' | 'collapsible'> {
  children?: React.ReactNode
  actions?: React.ReactNode
  borderless?: boolean
  onToggleCollapse?: () => unknown
}

export function SectionHeader({
  collapsible,
  collapsed,
  borderless,
  onToggleCollapse,
  children,
  actions,
}: SectionHeaderProps) {
  const toggleCollapse = React.useCallback(
    () => onToggleCollapse && onToggleCollapse(),
    [onToggleCollapse],
  )
  return (
    <div className={cx(styles.header, borderless && styles.borderless)}>
      {collapsible && (
        <div className={styles.left}>
          <a
            className={cx(
              styles.btnCollapseToggle,
              collapsed && styles.collapsed,
            )}
            onClick={toggleCollapse}
          >
            <Icon name="arrow" />
          </a>
        </div>
      )}
      <div className={styles.headerContent}>{children}</div>
      {actions && <div className={styles.right}>{actions}</div>}
    </div>
  )
}

interface SectionTitleProps {
  children?: React.ReactNode
}

export function SectionTitle({ children }: SectionTitleProps) {
  return <p className={cx(styles.title)}>{children}</p>
}

interface SectionTitleButtonProps {
  children?: React.ReactNode
  icon?: React.ReactNode
  onClick?: () => unknown
  disabled?: boolean
  title?: string
}

export function SectionTitleButton({
  children,
  icon,
  onClick,
  disabled,
  title,
}: SectionTitleButtonProps) {
  return (
    <a
      className={cx(styles.titleButton, disabled && styles.disabled)}
      onClick={disabled ? undefined : onClick}
      title={title}
    >
      {children}
      {icon && <span className={styles.titleButtonIcon}>{icon}</span>}
    </a>
  )
}

interface SectionHeaderActionProps {
  children?: React.ReactNode
  disabled?: boolean
  title?: string
  active?: boolean
  onClick?: () => unknown
  tooltip?: string
  compact?: boolean
}

export function SectionHeaderAction({
  children,
  onClick,
  disabled,
  title,
  active,
  tooltip,
  compact,
}: SectionHeaderActionProps) {
  const element = (
    <a
      title={title}
      onClick={disabled ? undefined : onClick}
      className={cx(
        styles.headerAction,
        compact && styles.compact,
        disabled && styles.disabled,
        active && styles.active,
      )}
    >
      {children}
    </a>
  )
  return tooltip ? <Tooltip content={tooltip}>{element}</Tooltip> : element
}

interface SectionTitleSuffixProps {
  children?: React.ReactNode
}

export function SectionTitleSuffix({ children }: SectionTitleSuffixProps) {
  return <span className={cx(styles.titleSuffix)}>{children}</span>
}

interface SectionContentProps {
  children?: React.ReactNode
  collapsed?: boolean
}

export function SectionContent({ children, collapsed }: SectionContentProps) {
  return collapsed ? (
    <></>
  ) : (
    <div className={cx(styles.sectionContent)}>{children}</div>
  )
}

export interface SectionController {
  collapsed: boolean
  collapsible: boolean
  onToggleCollapse: () => unknown
  expand: () => unknown
  collapse: () => unknown
}

/**
 * Hook for easily managing sidebar collapse state.
 */
export function useSection(collapseInitially?: boolean): SectionController {
  const [collapsed, setCollapsed] = React.useState(collapseInitially || false)
  const onToggleCollapse = React.useCallback(() => setCollapsed((c) => !c), [])
  const collapse = React.useCallback(() => setCollapsed(true), [])
  const expand = React.useCallback(() => setCollapsed(false), [])
  return React.useMemo(
    () => ({
      collapsed,
      onToggleCollapse,
      collapsible: true,
      collapse,
      expand,
    }),
    [collapsed],
  )
}
